import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { paths } from '../app/paths';
import { useCountdownFactory } from '../app/useCountdown';
import { useSession, useWeek, useWeekList } from '../api/queries';
import { usePickController } from '../api/usePicks';
import { asyncState } from './asyncState';
import * as screens from '../ui/screens/player';

/**
 * The picks route.
 *
 * All of the behaviour the brief cares about lives here — auto-save, retry, optimistic
 * rollback, lock countdowns, week switching. The screen below receives the result as plain
 * props and is responsible only for drawing it.
 */
export function PicksRoute() {
  const navigate = useNavigate();
  const params = useParams<{ weekId?: string }>();

  const session = useSession();
  const seasonId = session.data?.activeSeason?.id ?? null;
  const weekList = useWeekList(seasonId);

  // Default to the league's current week, but honour an explicit /picks/:weekId.
  const [selectedWeekId, setSelectedWeekId] = useState<string | null>(params.weekId ?? null);
  const currentWeekId = session.data?.currentWeek?.id ?? null;
  const weekId = selectedWeekId ?? currentWeekId;

  useEffect(() => {
    if (params.weekId && params.weekId !== selectedWeekId) setSelectedWeekId(params.weekId);
  }, [params.weekId, selectedWeekId]);

  const week = useWeek(weekId);
  const picks = usePickController(weekId);
  const countdownFor = useCountdownFactory();

  const games = week.data?.games ?? [];

  return (
    <screens.Picks
      {...asyncState(week)}
      week={week.data ?? null}
      pickStateFor={picks.stateFor}
      onPick={picks.setPick}
      onRetry={picks.retry}
      savingCount={picks.savingCount}
      countdownFor={(slateGameId) => {
        const game = games.find((g) => g.id === slateGameId);
        return game ? countdownFor(game.kickoffAt) : '';
      }}
      weeks={weekList.data ?? []}
      onSelectWeek={(next) => {
        setSelectedWeekId(next);
        navigate(paths.picksForWeek(next));
      }}
      resultsHref={paths.results}
    />
  );
}
